import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "@/redux/axios";
import { WebSocketEvent } from "@/utils/websocket";

// Types
export interface EvaluationUploadData {
  file_url: string;
  paper: string | null;
  exam_type: string;
}

export interface EvaluationResponse {
  id?: string;
  status?: string;
  message?: string;
  evaluation_id?: string;
  task_id?: string;
  [key: string]: any;
}

export interface WebSocketState {
  isConnected: boolean;
  taskId: string | null;
  events: WebSocketEvent[];
  isProcessing: boolean;
  isComplete: boolean;
  error: string | null;
  evaluationId: string | null; // Store evaluation_id when received from WebSocket
}

interface EvaluateState {
  isUploading: boolean;
  isSubmitting: boolean;
  uploadProgress: number;
  isError: boolean;
  errorMessage: string;
  isSuccess: boolean;
  evaluationId: string | null;
  fileUrl: string | null;
  websocket: WebSocketState;
}

const initialState: EvaluateState = {
  isUploading: false,
  isSubmitting: false,
  uploadProgress: 0,
  isError: false,
  errorMessage: "",
  isSuccess: false,
  evaluationId: null,
  fileUrl: null,
  websocket: {
    isConnected: false,
    taskId: null,
    events: [],
    isProcessing: false,
    isComplete: false,
    error: null,
    evaluationId: null,
  },
};

// Upload evaluation API call
export const uploadEvaluation = createAsyncThunk(
  "evaluate/uploadEvaluation",
  async (data: EvaluationUploadData, thunkAPI) => {
    try {
      console.log("🚀 Starting API call to /evaluations/upload");
      console.log("📋 Request data:", data);
      console.log("🌐 Base URL:", axios.defaults.baseURL);

      const response = await axios.post("/evaluations/upload", data);

      console.log("✅ API Response Status:", response.status);
      console.log("📦 API Response Data:", response.data);

      // Accept 200, 201, and 202 (Accepted - processing started in background)
      if (
        response.status === 200 ||
        response.status === 201 ||
        response.status === 202
      ) {
        console.log("✅ Valid response status, returning data");
        return response.data;
      } else {
        console.warn("⚠️ Unexpected status code:", response.status);
        return thunkAPI.rejectWithValue(response.data);
      }
    } catch (err: any) {
      console.error("❌ API Error:", err);
      console.error("❌ Error Response:", err.response);
      console.error("❌ Error Message:", err.message);

      if (!err.response) {
        console.error(
          "❌ No response from server. Network error or server unreachable."
        );
        throw err;
      }

      const errorData = err.response.data || {
        message: "Failed to upload evaluation",
      };
      console.error("❌ Error Data:", errorData);

      return thunkAPI.rejectWithValue(errorData);
    }
  }
);

export const evaluateSlice = createSlice({
  name: "evaluate",
  initialState,
  reducers: {
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    setUploading: (state, action) => {
      state.isUploading = action.payload;
    },
    setFileUrl: (state, action) => {
      state.fileUrl = action.payload;
    },
    clearError: (state) => {
      state.isError = false;
      state.errorMessage = "";
    },
    clearSuccess: (state) => {
      state.isSuccess = false;
    },
    resetEvaluateState: (state) => {
      state.isUploading = false;
      state.isSubmitting = false;
      state.uploadProgress = 0;
      state.isError = false;
      state.errorMessage = "";
      state.isSuccess = false;
      state.evaluationId = null;
      state.fileUrl = null;
      // Reset WebSocket state
      state.websocket = {
        isConnected: false,
        taskId: null,
        events: [],
        isProcessing: false,
        isComplete: false,
        error: null,
        evaluationId: null,
      };
    },
    // WebSocket actions
    setWebSocketTaskId: (state, action) => {
      state.websocket.taskId = action.payload;
    },
    setWebSocketConnected: (state, action) => {
      state.websocket.isConnected = action.payload;
    },
    setWebSocketProcessing: (state, action) => {
      state.websocket.isProcessing = action.payload;
    },
    addWebSocketEvent: (state, action) => {
      state.websocket.events.push(action.payload);
    },
    setWebSocketComplete: (state, action) => {
      state.websocket.isComplete = action.payload;
      state.websocket.isProcessing = !action.payload;
    },
    setWebSocketError: (state, action) => {
      state.websocket.error = action.payload;
      state.websocket.isProcessing = false;
    },
    clearWebSocketEvents: (state) => {
      state.websocket.events = [];
    },
    setWebSocketEvaluationId: (state, action) => {
      state.websocket.evaluationId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload Evaluation
      .addCase(uploadEvaluation.pending, (state) => {
        state.isSubmitting = true;
        state.isError = false;
        state.errorMessage = "";
        state.isSuccess = false;
      })
      .addCase(uploadEvaluation.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.isSuccess = true;
        state.evaluationId =
          action.payload?.id || action.payload?.evaluation_id || null;
        state.isError = false;
        // Set task_id for WebSocket connection
        if (action.payload?.task_id) {
          console.log(
            "🔌 Setting task_id for WebSocket:",
            action.payload.task_id
          );
          state.websocket.taskId = action.payload.task_id;
          state.websocket.isProcessing = true;
          state.websocket.isComplete = false;
          state.websocket.events = []; // Clear previous events
          state.websocket.error = null; // Clear any previous errors
        } else {
          console.warn("⚠️ No task_id in response:", action.payload);
        }
      })
      .addCase(uploadEvaluation.rejected, (state, action: any) => {
        state.isSubmitting = false;
        state.isError = true;
        state.errorMessage =
          action.payload?.message || "Failed to upload evaluation";
        state.isSuccess = false;
      });
  },
});

export const {
  setUploadProgress,
  setUploading,
  setFileUrl,
  clearError,
  clearSuccess,
  resetEvaluateState,
  setWebSocketTaskId,
  setWebSocketConnected,
  setWebSocketProcessing,
  addWebSocketEvent,
  setWebSocketComplete,
  setWebSocketError,
  clearWebSocketEvents,
  setWebSocketEvaluationId,
} = evaluateSlice.actions;

export const evaluateSelector = (state: any) => state.evaluate;

export const evaluateReducer = evaluateSlice.reducer;
export default evaluateSlice.reducer;
